import { put, call, takeLatest } from "redux-saga/effects";
import * as Actions from "us.collection.admin/actions";
import * as API from "us.collection.admin/services";
import { ExtendedFieldsActionTypes } from "us.collection.admin/constants/Actions";
import { formatSPList } from "us.collection.admin/functions";
import _ from "lodash";
import Common from "us.common";
import { i18n } from "us.helper";

const { $MessageBox, $Message } = Common.Components;

const { extendedFields } = Actions;
const { get, save, deleteField, getSPList, getDataTypes } = API.extendedFields;
const {
  fields,
  storedProcedures,
  newExtendedField,
  extendedFieldDetails,
  deleteExtendedField,
  dataTypes,
} = extendedFields;
const {
  GET_EXTENDED_FIELDS,
  GET_EXTENDED_FIELD_SP_LIST,
  NEW_EXTENDED_FIELD,
  UPDATE_EXTENDED_FIELD,
  DELETE_EXTENDED_FIELD,
  GET_DATA_TYPES,
} = ExtendedFieldsActionTypes;

export const ExtendedFieldsSagas = {
  fieldList: {
    get: function* (): any {
      try {
        const { data, status } = yield call(get);
        if (Array.isArray(data) && status === 200) {
          yield put(fields.success(data));
        } else {
          yield put(fields.fail(new Error()));
        }
      } catch (error) {
        yield put(fields.fail(new Error()));
      }
    },
    new: function* (action: any): any {
      const params = action?.payload?.data ?? {};
      try {
        const { data, status } = yield call(save, params);
        if (status === 200 || status === 201) {
          yield put(newExtendedField.success(data));
          $MessageBox(
            "success",
            "US.COLLECTION.ADMIN:EXTENDED_FIELDS.EXTENDED_FIELD_HAS_BEEN_ADDED_SUCCESSFULLY",
            "",
            ""
          );

          if (fields.get) yield put(fields.get({}));
          if (fields.openDrawer) yield put(fields.openDrawer({}));
        } else if (status === 401) {
          yield put(newExtendedField.fail(new Error()));
          $MessageBox(
            "error",
            "US.COLLECTION.ADMIN:EXTENDED_FIELDS.NO_PRIVILEGE_TO_ADD",
            "",
            ""
          );
        } else {
          yield put(newExtendedField.fail(new Error()));
          $MessageBox(
            "error",
            "US.COLLECTION.ADMIN:EXTENDED_FIELDS.FAILED_TO_ADD_EXTENDED_FIELD",
            "",
            ""
          );
        }
      } catch (error) {
        yield put(newExtendedField.fail(new Error()));
        $MessageBox(
          "error",
          "US.COLLECTION.ADMIN:EXTENDED_FIELDS.FAILED_TO_ADD_EXTENDED_FIELD",
          "",
          ""
        );
      }
    },
    update: function* (action: any): any {
      const params = action?.payload?.data ?? {};
      try {
        const { data, status } = yield call(save, params);
        if (status === 200 || status === 201) {
          yield put(extendedFieldDetails.success(data));
          $MessageBox(
            "success",
            "US.COLLECTION.ADMIN:EXTENDED_FIELDS.EXTENDED_FIELD_HAS_BEEN_UPDATED_SUCCESSFULLY",
            "",
            ""
          );

          if (fields.get) yield put(fields.get({}));
          if (fields.openDrawer) yield put(fields.openDrawer({}));
        } else if (status === 401) {
          yield put(extendedFieldDetails.fail(new Error()));
          $MessageBox(
            "error",
            "US.COLLECTION.ADMIN:EXTENDED_FIELDS.NO_PRIVILEGE_TO_EDIT",
            "",
            ""
          );
        } else {
          yield put(extendedFieldDetails.fail(new Error()));
          $MessageBox(
            "error",
            "US.COLLECTION.ADMIN:EXTENDED_FIELDS.FAILED_TO_UPDATE_EXTENDED_FIELD",
            "",
            ""
          );
        }
      } catch (error) {
        yield put(extendedFieldDetails.fail(new Error()));
        $MessageBox(
          "error",
          "US.COLLECTION.ADMIN:EXTENDED_FIELDS.FAILED_TO_UPDATE_EXTENDED_FIELD",
          "",
          ""
        );
      }
    },
    delete: function* (action: any): any {
      const params = action?.payload?.data ?? {};
      $Message.loading(i18n.t("COMMON.DELETING") as string, 0);

      try {
        const { data, status } = yield call(deleteField, params);
        if (status === 200) {
          yield put(deleteExtendedField.success(data));
          $Message.destroy();
          $MessageBox(
            "success",
            "US.COLLECTION.ADMIN:EXTENDED_FIELDS.EXTENDED_FIELD_HAS_BEEN_DELETED_SUCCESSFULLY",
            "",
            ""
          );

          if (fields.get) yield put(fields.get(params?.debtorNo));
        } else if (status === 401) {
          yield put(deleteExtendedField.fail(new Error()));
          $Message.destroy();
          $MessageBox(
            "error",
            "US.COLLECTION.ADMIN:EXTENDED_FIELDS.NO_PRIVILEGE_TO_DELETE",
            "",
            ""
          );
        } else {
          yield put(deleteExtendedField.fail(new Error()));
          $Message.destroy();
          $MessageBox(
            "error",
            "US.COLLECTION.ADMIN:EXTENDED_FIELDS.FAILED_TO_DELETE_EXTENDED_FIELD",
            "",
            ""
          );
        }
      } catch (error) {
        yield put(deleteExtendedField.fail(new Error()));
        $Message.destroy();
        $MessageBox(
          "error",
          "US.COLLECTION.ADMIN:EXTENDED_FIELDS.FAILED_TO_DELETE_EXTENDED_FIELD",
          "",
          ""
        );
      }
    },
  },
  storedProcedureList: {
    get: function* (): any {
      try {
        const { data, status } = yield call(getSPList);
        if (Array.isArray(data) && status === 200) {
          yield put(storedProcedures.success(formatSPList(data)));
        } else {
          yield put(storedProcedures.fail(new Error()));
        }
      } catch (error) {
        yield put(storedProcedures.fail(new Error()));
      }
    },
  },
  dataTypes: {
    get: function* (): any {
      try {
        const { data, status } = yield call(getDataTypes);
        if (Array.isArray(data) && status === 200) {
          yield put(dataTypes.success(data));
        } else {
          yield put(dataTypes.fail(new Error()));
        }
      } catch (error) {
        yield put(dataTypes.fail(new Error()));
      }
    },
  },
};

export default [
  takeLatest(GET_EXTENDED_FIELDS, ExtendedFieldsSagas.fieldList.get),
  takeLatest(
    GET_EXTENDED_FIELD_SP_LIST,
    ExtendedFieldsSagas.storedProcedureList.get
  ),
  takeLatest(NEW_EXTENDED_FIELD, ExtendedFieldsSagas.fieldList.new),
  takeLatest(UPDATE_EXTENDED_FIELD, ExtendedFieldsSagas.fieldList.update),
  takeLatest(DELETE_EXTENDED_FIELD, ExtendedFieldsSagas.fieldList.delete),
  takeLatest(GET_DATA_TYPES, ExtendedFieldsSagas.dataTypes.get),
];
